import * as React from "react"
import { Check, ChevronDown, Plus } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { TagBadge, type Tag } from "@/components/tags/TagBadge"
import { TagColorPicker, TagIconPicker } from "@/components/tags/TagFieldPickers"
import { TAG_COLOR_NAMES } from "@/lib/tag-styles"
import { TAG_ICON_NAMES } from "@/lib/tag-icons"

// A tag to be created alongside the piece. No id yet — the controller creates
// it (idempotent on name) and attaches it. Mirrors the `new_tags` param shape.
export type NewTag = { name: string; color: string; icon: string }

// The picker's whole value: ids of existing tags to attach + specs for tags
// to create-and-attach. This maps 1:1 onto the `tag_ids` / `new_tags` params.
export type TagSelection = { tagIds: number[]; newTags: NewTag[] }

export const EMPTY_TAG_SELECTION: TagSelection = { tagIds: [], newTags: [] }

type Props = {
  available: Tag[]
  value: TagSelection
  onChange: (next: TagSelection) => void
}

// Multi-select tag control with inline tag creation. Selected tags (existing
// + about-to-be-created) render as removable badges; a dropdown lists the
// remaining tags to attach and offers a small create-a-new-tag form.
export function TagPicker({ available, value, onChange }: Props) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const rootRef = React.useRef<HTMLDivElement>(null)

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  const byId = React.useMemo(() => new Map(available.map((t) => [t.id, t])), [available])
  const selectedTags = value.tagIds.map((id) => byId.get(id)).filter((t): t is Tag => Boolean(t))

  const toggleExisting = (id: number) => {
    const has = value.tagIds.includes(id)
    onChange({
      ...value,
      tagIds: has ? value.tagIds.filter((x) => x !== id) : [...value.tagIds, id],
    })
  }
  const removeNew = (index: number) =>
    onChange({ ...value, newTags: value.newTags.filter((_, i) => i !== index) })

  const addNew = (tag: NewTag) => onChange({ ...value, newTags: [...value.newTags, tag] })

  const unselected = available.filter(
    (t) => !value.tagIds.includes(t.id) && t.name.toLowerCase().includes(query.trim().toLowerCase()),
  )

  const hasAnySelected = selectedTags.length > 0 || value.newTags.length > 0

  return (
    <div ref={rootRef} className="relative flex flex-col gap-2">
      {hasAnySelected && (
        <div className="flex flex-wrap gap-1.5">
          {selectedTags.map((t) => (
            <TagBadge key={`e-${t.id}`} tag={t} onRemove={() => toggleExisting(t.id)} />
          ))}
          {value.newTags.map((t, i) => (
            <TagBadge
              key={`n-${i}`}
              tag={{ id: -(i + 1), name: t.name, color: t.color, icon: t.icon }}
              onRemove={() => removeNew(i)}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex w-fit items-center gap-1.5 rounded-md border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body hover:bg-surface"
      >
        <Plus className="h-3.5 w-3.5" />
        <span>Add tags</span>
        <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />
      </button>

      {open && (
        <div className="absolute top-full z-50 mt-1 w-72 rounded-md border border-hairline bg-page p-2 shadow-lg">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tags…"
            className="h-8 text-sm"
          />

          <div className="mt-2 max-h-48 overflow-y-auto">
            {unselected.length === 0 ? (
              <p className="px-1 py-2 text-xs text-ink-muted">
                {available.length === 0 ? "No tags yet — create one below." : "No matching tags."}
              </p>
            ) : (
              <ul className="flex flex-col">
                {unselected.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => toggleExisting(t.id)}
                      className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left hover:bg-surface"
                    >
                      <TagBadge tag={t} />
                      <Check className="h-3.5 w-3.5 opacity-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-2 border-t border-hairline pt-2">
            <InlineCreate onCreate={addNew} />
          </div>
        </div>
      )}
    </div>
  )
}

// The "create a new tag" mini-form tucked into the picker dropdown: name +
// color + icon. On add it hands the spec up and resets.
function InlineCreate({ onCreate }: { onCreate: (tag: NewTag) => void }) {
  const [expanded, setExpanded] = React.useState(false)
  const [name, setName] = React.useState("")
  const [color, setColor] = React.useState<string>(TAG_COLOR_NAMES[0])
  const [icon, setIcon] = React.useState<string>(TAG_ICON_NAMES[0])

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-sm text-accent-display hover:bg-surface"
      >
        <Plus className="h-3.5 w-3.5" />
        Create a new tag
      </button>
    )
  }

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onCreate({ name: trimmed, color, icon })
    setName("")
    setColor(TAG_COLOR_NAMES[0])
    setIcon(TAG_ICON_NAMES[0])
    setExpanded(false)
  }

  return (
    <div className="flex flex-col gap-2 p-1">
      <Input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            submit()
          }
        }}
        placeholder="Tag name"
        className="h-8 text-sm"
      />
      <TagColorPicker value={color} onChange={setColor} />
      <TagIconPicker value={icon} onChange={setIcon} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(false)}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={submit} disabled={name.trim().length === 0}>
          Add tag
        </Button>
      </div>
    </div>
  )
}
